"use client";

import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { setTheme as setThemeAction } from "@/store/slices/themeSlice";

type Theme = "light" | "dark" | "system";

function applyTheme(theme: Theme) {
  const root = document.documentElement;
  root.classList.remove("dark");
  if (theme === "dark") {
    root.classList.add("dark");
  } else if (theme === "system") {
    if (window.matchMedia("(prefers-color-scheme: dark)").matches) {
      root.classList.add("dark");
    }
  }
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const dispatch = useAppDispatch();
  const theme = useAppSelector((state) => state.theme.theme);

  // Initialise from localStorage on first mount
  useEffect(() => {
    const saved = (localStorage.getItem("theme") as Theme) || "dark";
    dispatch(setThemeAction(saved));
  }, [dispatch]);

  // Apply DOM class whenever theme changes in the store
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  return <>{children}</>;
}

export function useTheme() {
  const dispatch = useAppDispatch();
  const theme = useAppSelector((state) => state.theme.theme);

  function setTheme(newTheme: Theme) {
    localStorage.setItem("theme", newTheme);
    dispatch(setThemeAction(newTheme));
  }

  return { theme, setTheme };
}
